import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import dagre from 'dagre';
import Button from '../shared/Button';
import NewsModal from './NewsModal';
import { getMarket, getGraph, propagateNews } from '../../utils/api';
import 'reactflow/dist/style.css';
import './GraphView.css';

const nodeWidth = 180;
const nodeHeight = 60;

// Color constants (CSS variables don't work in React Flow edge styles)
const COLORS = {
  success: '#22c55e',
  error: '#ef4444',
  accent: '#B55C4A',
  gray: '#9ca3af',
};

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  // Create a new graph instance each time to avoid stale data
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, nodesep: 60, ranksep: 80 });

  nodes.forEach((node) => {
    // Use different heights for different node types
    const height = node.type === 'market' ? 80 : nodeHeight;
    const width = node.type === 'market' ? 300 : nodeWidth;
    dagreGraph.setNode(node.id, { width, height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const width = node.type === 'market' ? 300 : nodeWidth;
    const height = node.type === 'market' ? 80 : nodeHeight;
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Custom node component for atomics
const AtomicNode = ({ data }) => {
  const indicatorClass = data.relationship === 'positive'
    ? 'graph-node__indicator--positive'
    : 'graph-node__indicator--negative';

  let boxClass = 'graph-node graph-node--atomic';
  if (data.activated) boxClass += ' graph-node--activated';
  if (data.effect === 'up') boxClass += ' graph-node--up';
  if (data.effect === 'down') boxClass += ' graph-node--down';
  if (data.hasIntermediate) boxClass += ' graph-node--has-intermediate';

  return (
    <div className={boxClass}>
      <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
      <div className="graph-node__title">{data.label}</div>
      <div className={`graph-node__indicator ${indicatorClass}`}>
        {data.relationship === 'positive' ? '+' : '-'}
      </div>
      {data.effect && (
        <div className={`graph-node__effect graph-node__effect--${data.effect}`}>
          {data.effect === 'up' ? '▲' : '▼'}
        </div>
      )}
    </div>
  );
};

// Custom node component for intermediates
const IntermediateNode = ({ data }) => {
  const indicatorClass = data.relationship === 'positive'
    ? 'graph-node__indicator--positive'
    : 'graph-node__indicator--negative';

  return (
    <div className="graph-node graph-node--intermediate">
      <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
      <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
      <div className="graph-node__title">{data.label}</div>
      <div className={`graph-node__indicator ${indicatorClass}`}>
        {data.relationship === 'positive' ? '+' : '-'}
      </div>
    </div>
  );
};

// Custom node component for market
const MarketNode = ({ data }) => {
  let boxClass = 'graph-node graph-node--market';
  if (data.activated) boxClass += ' graph-node--activated';
  if (data.effect === 'bullish') boxClass += ' graph-node--bullish';
  if (data.effect === 'bearish') boxClass += ' graph-node--bearish';

  return (
    <div className={boxClass}>
      <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
      <div className="graph-node__title">{data.label}</div>
      {data.effectLabel && (
        <div className={`graph-node__market-effect graph-node__market-effect--${data.effect}`}>
          {data.effectLabel}
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  atomic: AtomicNode,
  intermediate: IntermediateNode,
  market: MarketNode,
};

const GraphView = () => {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const [market, setMarket] = useState(null);
  const [graph, setGraph] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [propagationResult, setPropagationResult] = useState(null);
  const [activatedNodes, setActivatedNodes] = useState(new Set());
  const [isPropagating, setIsPropagating] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [marketRes, graphRes] = await Promise.all([
          getMarket(marketId),
          getGraph(marketId),
        ]);
        setMarket(marketRes.market);
        setGraph(graphRes.graph);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [marketId]);

  // Build React Flow nodes and edges from graph data
  useEffect(() => {
    if (!graph || !market) return;

    const flowNodes = [];
    const flowEdges = [];

    // Get edge relationship helper
    const getEdgeRelationship = (fromId, toId) => {
      const edge = graph.edges?.find(e => e.source === fromId && e.target === toId);
      return edge?.relationship || 'positive';
    };

    // Find which atomics belong to which intermediate
    const atomicToIntermediate = {};
    const intermediateNodes = graph.nodes?.filter(n => n.type === 'intermediate') || [];
    intermediateNodes.forEach(inter => {
      (inter.components || []).forEach(compId => {
        atomicToIntermediate[compId] = inter.id;
      });
    });

    // Add intermediate nodes
    intermediateNodes.forEach((node) => {
      const relationship = getEdgeRelationship(node.id, 'MARKET');
      flowNodes.push({
        id: node.id,
        type: 'intermediate',
        data: {
          label: node.label,
          relationship,
        },
        position: { x: 0, y: 0 },
      });

      // Edge from intermediate to market
      const edgeColor = relationship === 'positive' ? COLORS.success : COLORS.error;
      flowEdges.push({
        id: `${node.id}-MARKET`,
        source: node.id,
        target: 'MARKET',
        type: 'smoothstep',
        style: {
          stroke: edgeColor,
          strokeWidth: 2,
          strokeDasharray: relationship === 'negative' ? '5,5' : undefined,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColor,
          width: 20,
          height: 20,
        },
        animated: activatedNodes.has(node.id),
      });
    });

    // Add atomic nodes
    const atomicNodes = graph.nodes?.filter(n => n.type === 'atomic') || [];
    atomicNodes.forEach((node) => {
      const intermediateId = atomicToIntermediate[node.id];
      const targetId = intermediateId || 'MARKET';
      const relationship = getEdgeRelationship(node.id, targetId);
      const affectedNode = propagationResult?.affected_nodes?.find(n => n.factor_id === node.id);

      flowNodes.push({
        id: node.id,
        type: 'atomic',
        data: {
          label: node.label,
          relationship,
          hasIntermediate: !!intermediateId,
          activated: activatedNodes.has(node.id),
          effect: affectedNode?.direction,
        },
        position: { x: 0, y: 0 },
      });

      // Edge from atomic to intermediate or market
      const atomicEdgeColor = relationship === 'positive' ? COLORS.success : COLORS.error;
      flowEdges.push({
        id: `${node.id}-${targetId}`,
        source: node.id,
        target: targetId,
        type: 'smoothstep',
        style: {
          stroke: atomicEdgeColor,
          strokeWidth: 2,
          strokeDasharray: relationship === 'negative' ? '5,5' : undefined,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: atomicEdgeColor,
          width: 20,
          height: 20,
        },
        animated: activatedNodes.has(node.id),
      });
    });

    // Add market node
    flowNodes.push({
      id: 'MARKET',
      type: 'market',
      data: {
        label: market.question,
        activated: activatedNodes.has('MARKET'),
        effect: propagationResult?.market_effect?.direction,
        effectLabel: propagationResult?.market_effect?.direction?.toUpperCase(),
      },
      position: { x: 0, y: 0 },
    });

    // Apply dagre layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      flowNodes,
      flowEdges,
      'TB'
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [graph, market, activatedNodes, propagationResult, setNodes, setEdges]);

  const handleNewsSubmit = async (newsText) => {
    setIsPropagating(true);
    setShowNewsModal(false);
    setPropagationResult(null);
    setActivatedNodes(new Set());

    try {
      const result = await propagateNews(marketId, newsText);
      setPropagationResult({
        newsText,
        ...result.propagation,
      });

      // Animate the activation of nodes
      const affectedIds = result.propagation.affected_nodes.map(n => n.factor_id);

      // Animate nodes one by one
      for (let i = 0; i < affectedIds.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 300));
        setActivatedNodes(prev => new Set([...prev, affectedIds[i]]));
      }

      // Then show market effect
      await new Promise(resolve => setTimeout(resolve, 500));
      setActivatedNodes(prev => new Set([...prev, 'MARKET']));

    } catch (err) {
      setError(err.message);
    } finally {
      setIsPropagating(false);
    }
  };

  const clearPropagation = () => {
    setPropagationResult(null);
    setActivatedNodes(new Set());
  };

  const intermediateFactors = useMemo(() => {
    return graph?.nodes?.filter(n => n.type === 'intermediate') || [];
  }, [graph]);

  if (loading) {
    return (
      <div className="graph-view">
        <div className="graph-view__loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="graph-view">
        <div className="graph-view__error">
          <h2>Error</h2>
          <p>{error}</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="graph-view">
      <header className="graph-view__header">
        <div className="graph-view__header-left">
          <img src="/logo.png" alt="Composer" className="graph-view__logo" />
          <div className="graph-view__header-text">
            <h1 className="graph-view__title">Composer</h1>
            <p className="graph-view__subtitle">Factor Graph</p>
          </div>
        </div>
        <div className="graph-view__header-right">
          <Button
            onClick={() => setShowNewsModal(true)}
            disabled={isPropagating}
          >
            {isPropagating ? 'Propagating...' : 'Introduce News'}
          </Button>
        </div>
      </header>

      <main className="graph-view__main">
        <div className="graph-view__canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            attributionPosition="bottom-left"
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag={true}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            defaultEdgeOptions={{
              type: 'smoothstep',
              style: { strokeWidth: 2 },
            }}
            minZoom={0.5}
            maxZoom={1.5}
          >
            <Background color="#e5e5e5" gap={20} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>

          {/* Legend */}
          <div className="graph-view__legend">
            <div className="graph-view__legend-item">
              <span className="graph-view__legend-line graph-view__legend-line--positive"></span>
              <span>Positive correlation</span>
            </div>
            <div className="graph-view__legend-item">
              <span className="graph-view__legend-line graph-view__legend-line--negative"></span>
              <span>Negative correlation</span>
            </div>
          </div>
        </div>

        {/* Propagation Result */}
        {propagationResult && (
          <div className="graph-view__result">
            <div className="graph-view__result-header">
              <h3>News Propagation</h3>
              <button onClick={clearPropagation} className="graph-view__result-close">Clear</button>
            </div>

            <div className="graph-view__result-news">
              <span className="graph-view__result-label">News:</span>
              <span className="graph-view__result-text">{propagationResult.newsText}</span>
            </div>

            <div className="graph-view__result-effect">
              <span className={`graph-view__result-badge graph-view__result-badge--${propagationResult.market_effect?.direction}`}>
                {propagationResult.market_effect?.direction?.toUpperCase()}
              </span>
            </div>

            <div className="graph-view__result-reasoning">
              {propagationResult.market_effect?.reasoning}
            </div>

            {propagationResult.market_effect?.activated_paths?.length > 0 && (
              <div className="graph-view__result-paths">
                <span className="graph-view__result-label">Activated Paths:</span>
                {propagationResult.market_effect.activated_paths.map((path, i) => (
                  <div key={i} className="graph-view__result-path">{path}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Factor Details */}
        <div className="graph-view__details">
          <h3>Factor Details</h3>
          <div className="graph-view__factors">
            {intermediateFactors.map(node => (
              <div key={node.id} className="graph-view__factor graph-view__factor--intermediate">
                <div className="graph-view__factor-header">
                  <span className="graph-view__factor-type graph-view__factor-type--intermediate">INTERMEDIATE</span>
                  <span className="graph-view__factor-name">{node.label}</span>
                </div>
                <p className="graph-view__factor-description">{node.description}</p>
                {node.components?.length > 0 && (
                  <div className="graph-view__factor-components">
                    Components: {node.components.join(', ')}
                  </div>
                )}
                {node.grouping_rationale && (
                  <div className="graph-view__factor-rationale">
                    {node.grouping_rationale}
                  </div>
                )}
              </div>
            ))}
            {graph?.nodes?.filter(n => n.type === 'atomic').map(node => (
              <div key={node.id} className="graph-view__factor">
                <div className="graph-view__factor-header">
                  <span className="graph-view__factor-type">ATOMIC</span>
                  <span className="graph-view__factor-name">{node.label}</span>
                </div>
                <p className="graph-view__factor-description">{node.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {showNewsModal && (
        <NewsModal
          onClose={() => setShowNewsModal(false)}
          onSubmit={handleNewsSubmit}
          marketQuestion={market?.question}
          factors={graph?.nodes?.filter(n => n.type === 'atomic' || n.type === 'intermediate') || []}
        />
      )}
    </div>
  );
};

export default GraphView;
